import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toastActions, TOAST_STATUS } from '@leagueplatform/toast-messages';
import { patchCommunicationPreferences } from 'common/communication-preferences.api';
import { COMMUNICATION_PREFERENCES_QUERY_KEY } from './use-communication-preferences-query.hook';

/**
 * Persists a delta of changed preferences (GEIS-359).
 *
 * Invalidate-on-success (no optimistic update): on a successful PATCH the
 * preferences query is invalidated so the form re-seeds from the server.
 * Save feedback surfaces as a bottom-of-screen dismissible toast (success and
 * error) and is non-blocking — the form stays usable.
 */
export const useSaveCommunicationPreferences = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['saveCommunicationPreferences'],
    mutationFn: patchCommunicationPreferences,
    onSuccess: () => {
      toastActions.add({
        type: TOAST_STATUS.SUCCESS,
        textId: 'STR_COMM_PREF_SAVE_SUCCESS',
      });
      // Return the invalidation promise so the mutation stays pending until the
      // refetch settles — the form re-seeds (and Save re-disables) from fresh
      // server data before the save resolves. Mirrors useUpdatePreferredName.
      return queryClient.invalidateQueries({
        queryKey: COMMUNICATION_PREFERENCES_QUERY_KEY,
      });
    },
    onError: () =>
      toastActions.add({
        type: TOAST_STATUS.ERROR,
        textId: 'STR_COMM_PREF_SAVE_ERROR',
      }),
  });
};
